import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  AppState,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Colors, Radius, Shadow, Typography, Spacing } from '../theme';
import { useI18n } from '../i18n';

// Matches the server's own per-uid cooldown (functions/index.js
// RATE_LIMIT_COOLDOWN_MS) — purely a UX nicety so the button visibly
// disables instead of the user tapping it and getting a resource-exhausted
// error back. The server enforces the real limit regardless of this.
const RESEND_COOLDOWN_SECONDS = 60;

interface Props {
  email: string | null;
  onResend: () => Promise<void>;
  onCheckAgain: () => Promise<boolean>;
  onSignOut: () => Promise<void>;
  onContinueBrowsing?: () => void;
}

export default function VerifyEmailScreen({ email, onResend, onCheckAgain, onSignOut, onContinueBrowsing }: Props) {
  const insets = useSafeAreaInsets();
  const { t } = useI18n();
  const [checking, setChecking] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone: 'error' | 'success' } | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
  }, []);

  const startCooldown = useCallback(() => {
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds(current => {
        if (current <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }, []);

  const handleResend = useCallback(async () => {
    setMessage(null);
    setResending(true);
    try {
      await onResend();
      setMessage({ text: t('verifyEmail.resendSuccess'), tone: 'success' });
      startCooldown();
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : t('verifyEmail.resendFailure'), tone: 'error' });
      // The account may already be rate-limited server-side even if this
      // client's local cooldown was reset (app restart, etc.) — reflect
      // that in the UI either way so the button doesn't look tappable again.
      startCooldown();
    } finally {
      setResending(false);
    }
  }, [onResend, startCooldown, t]);

  const handleCheckAgain = useCallback(async () => {
    setMessage(null);
    setChecking(true);
    try {
      const verified = await onCheckAgain();
      if (verified) {
        setMessage({ text: t('verifyEmail.verifiedSuccess'), tone: 'success' });
      } else {
        setMessage({ text: t('verifyEmail.stillNotVerifiedMessage'), tone: 'error' });
      }
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : t('common.somethingWentWrong'), tone: 'error' });
    } finally {
      setChecking(false);
    }
  }, [onCheckAgain, t]);

  // The Firebase-hosted verification link often opens in an external
  // browser rather than the app, so there's no guaranteed deep-link return —
  // silently re-check whenever the app comes back to the foreground, as a
  // convenience on top of the always-available manual "Check again" button.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', nextState => {
      if (nextState === 'active') {
        void onCheckAgain().catch(() => undefined);
      }
    });
    return () => subscription.remove();
  }, [onCheckAgain]);

  return (
    <LinearGradient colors={['#0B1826', '#070B14', '#020409']} style={styles.container}>
      <StatusBar barStyle="light-content" translucent backgroundColor="transparent" />
      <ScrollView
        contentContainerStyle={[styles.scroll, { paddingTop: insets.top + 36, paddingBottom: insets.bottom + 28 }]}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.card}>
          <View style={styles.iconWrap}>
            <Icon name="mail" size={28} color="#FF7A00" />
          </View>
          <Text style={styles.title}>{t('verifyEmail.title')}</Text>
          <Text style={styles.subtitle}>{t('verifyEmail.subtitle')}</Text>
          {email ? <Text style={styles.email}>{email}</Text> : null}
          <Text style={styles.instructions}>{t('verifyEmail.instructions')}</Text>

          {message ? (
            <Text style={[styles.message, message.tone === 'error' ? styles.messageError : styles.messageSuccess]}>
              {message.text}
            </Text>
          ) : null}

          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleCheckAgain}
            activeOpacity={0.84}
            disabled={checking}
          >
            {checking ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={styles.primaryButtonText}>{t('verifyEmail.checkAgain')}</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={handleResend}
            activeOpacity={0.84}
            disabled={resending || cooldownSeconds > 0}
          >
            {resending ? (
              <ActivityIndicator size="small" color={Colors.textPrimary} />
            ) : (
              <Text style={styles.secondaryButtonText}>
                {cooldownSeconds > 0
                  ? t('verifyEmail.resendCooldown').replace('{count}', String(cooldownSeconds))
                  : t('verifyEmail.resend')}
              </Text>
            )}
          </TouchableOpacity>

          {onContinueBrowsing ? (
            <TouchableOpacity onPress={onContinueBrowsing} style={styles.secondaryButton}>
              <Text style={styles.secondaryButtonText}>{t('verifyEmail.continueBrowsing')}</Text>
            </TouchableOpacity>
          ) : null}

          <TouchableOpacity onPress={() => void onSignOut()} style={styles.signOutButton}>
            <Text style={styles.signOutText}>{t('verifyEmail.logout')}</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.bg },
  scroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 20,
  },
  card: {
    width: '100%',
    maxWidth: 430,
    alignSelf: 'center',
    padding: 24,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: Colors.border,
    backgroundColor: 'rgba(9,17,29,0.92)',
    alignItems: 'center',
    ...Shadow.strong,
  },
  iconWrap: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,122,0,0.12)',
    marginBottom: Spacing.md,
  },
  title: {
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
    textAlign: 'center',
  },
  subtitle: {
    marginTop: 10,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 21,
    textAlign: 'center',
  },
  email: {
    marginTop: 4,
    color: Colors.textPrimary,
    fontSize: Typography.base,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  instructions: {
    marginTop: 14,
    color: Colors.textSecondary,
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
  message: {
    width: '100%',
    marginTop: 18,
    padding: 12,
    borderRadius: Radius.lg,
    fontSize: Typography.sm,
    lineHeight: 20,
    textAlign: 'center',
  },
  messageError: {
    color: '#FECDD3',
    backgroundColor: 'rgba(244,63,94,0.09)',
  },
  messageSuccess: {
    color: '#BBF7D0',
    backgroundColor: 'rgba(34,197,94,0.10)',
  },
  primaryButton: {
    width: '100%',
    minHeight: 52,
    marginTop: 24,
    borderRadius: Radius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FF7A00',
    ...Shadow.glow,
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.extrabold,
  },
  secondaryButton: {
    width: '100%',
    minHeight: 48,
    marginTop: 14,
    borderRadius: Radius.lg,
    borderWidth: 1,
    borderColor: Colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.06)',
  },
  secondaryButtonText: {
    color: Colors.textPrimary,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  signOutButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  signOutText: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    textDecorationLine: 'underline',
  },
});
